import React, { useMemo, useState } from "react";
import { DateTime } from "luxon";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import { useTheme } from "@mui/material/styles";

import {
    GiornoCalendario,
    CaricoRepartoDettaglio,
    GiornoCalendarioDettaglio,
    CaricoRepartoGiorno,
    CaricoOperatoreGiorno,
    PlaceholderInfo,
} from "../caricoRepartiSlice";
import { SENZA_OPERATORE_ID } from "./GanttHeader";

// ============================================
// INTERFACES
// ============================================

interface GanttSettimanaProps {
    giorni: DateTime[]; // 5 giorni (lun-ven)
    calendario?: GiornoCalendario[];
    repartiSelezionati?: number[];
    operatoriSelezionati?: number[];
    caricoDettaglio?: { [repartoId: number]: CaricoRepartoDettaglio };
}

// ============================================
// MINI PIE CHART SVG
// ============================================

interface MiniPieProps {
    percentuale: number; // 0-100+
    size?: number;
    allarme?: boolean;
}

export const MiniPie = ({ percentuale, size = 20, allarme = false }: MiniPieProps) => {
    const theme = useTheme();
    const raggio = (size - 2) / 2;
    const centro = size / 2;
    const circonferenza = 2 * Math.PI * raggio;
    const percCapped = Math.min(percentuale, 100);
    const offset = circonferenza - (percCapped / 100) * circonferenza;

    // Colore in base alla percentuale
    let colore = theme.palette.success.main; // verde: < 80%
    if (percentuale >= 100) {
        colore = theme.palette.error.main; // rosso: >= 100%
    } else if (percentuale >= 80) {
        colore = theme.palette.warning.main; // arancione: 80-99%
    }
    if (allarme) {
        colore = theme.palette.error.main;
    }

    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle
                cx={centro}
                cy={centro}
                r={raggio}
                fill="none"
                stroke={theme.palette.grey[300]}
                strokeWidth={2}
            />
            {percentuale > 0 && (
                <circle
                    cx={centro}
                    cy={centro}
                    r={raggio}
                    fill="none"
                    stroke={colore}
                    strokeWidth={2}
                    strokeDasharray={`${circonferenza}`}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    transform={`rotate(-90 ${centro} ${centro})`}
                />
            )}
        </svg>
    );
};

// ============================================
// POPOVER DETTAGLIO REPARTO
// ============================================

interface PopoverCaricoRepartoProps {
    anchorEl: HTMLElement | null;
    onClose: () => void;
    codiceReparto: string;
    descrizioneReparto: string;
    giornoDettaglio?: GiornoCalendarioDettaglio;
    giornoCalendario?: CaricoRepartoGiorno;
}

const PopoverCaricoReparto = ({
    anchorEl,
    onClose,
    codiceReparto,
    descrizioneReparto,
    giornoDettaglio,
    giornoCalendario,
}: PopoverCaricoRepartoProps) => {

    // Dati aggregati del reparto
    const oreDisponibili = giornoDettaglio?.ore_disponibili ?? giornoCalendario?.ore_disponibili ?? 0;
    const oreSchedulate = giornoDettaglio?.ore_schedulate ?? giornoCalendario?.ore_schedulate ?? 0;
    const allarme = giornoDettaglio?.allarme ?? giornoCalendario?.allarme ?? false;
    const percentuale = oreDisponibili > 0 ? (oreSchedulate / oreDisponibili) * 100 : 0;

    // Carico generico = ore_schedulate del reparto non attribuite ad alcun operatore.
    // Backend: incluso in ore_schedulate del reparto, escluso da quello dei singoli operatori.
    const oreGeneriche = giornoDettaglio
        ? Math.max(
              0,
              giornoDettaglio.ore_schedulate -
                  giornoDettaglio.operatori.reduce((sum, op) => sum + op.ore_schedulate, 0)
          )
        : 0;

    return (
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            slotProps={{
                paper: {
                    sx: { p: 1.5, minWidth: 200, maxWidth: 280 },
                },
            }}
        >
            {/* Header reparto */}
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
                <MiniPie percentuale={percentuale} size={28} allarme={allarme} />
                <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                        {codiceReparto}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {descrizioneReparto}
                    </Typography>
                </Box>
            </Stack>

            {/* Riepilogo ore */}
            <Stack direction="row" spacing={2} sx={{ mb: 0.5 }}>
                <Typography variant="caption">
                    Disponibili: <strong>{oreDisponibili.toFixed(1)}h</strong>
                </Typography>
                <Typography variant="caption">
                    Schedulate: <strong>{oreSchedulate.toFixed(1)}h</strong>
                </Typography>
            </Stack>

            {/* Dettaglio operatori + carico generico (se disponibili) */}
            {giornoDettaglio && (giornoDettaglio.operatori.length > 0 || oreGeneriche > 0) && (
                <>
                    <Divider sx={{ my: 0.5 }} />
                    {giornoDettaglio.operatori.length > 0 && (
                        <Typography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.25 }}>
                            Operatori
                        </Typography>
                    )}
                    {giornoDettaglio.operatori.map((op) => (
                        <Stack
                            key={op.operatore_id}
                            direction="row"
                            spacing={0.5}
                            sx={{
                                alignItems: "center",
                                py: 0.25,
                                opacity: op.assente ? 0.5 : 1,
                            }}
                        >
                            <MiniPie
                                percentuale={op.ore_disponibili > 0 ? (op.ore_schedulate / op.ore_disponibili) * 100 : 0}
                                size={14}
                                allarme={op.allarme}
                            />
                            <Typography
                                variant="caption"
                                sx={{
                                    flexGrow: 1,
                                    textDecoration: op.assente ? "line-through" : "none",
                                }}
                            >
                                {op.nominativo}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {op.ore_schedulate.toFixed(1)}/{op.ore_disponibili.toFixed(1)}h
                            </Typography>
                            {op.assente && (
                                <Typography variant="caption" color="error.main" fontWeight="bold">
                                    Assente
                                </Typography>
                            )}
                        </Stack>
                    ))}
                    {oreGeneriche > 0 && (
                        <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{ alignItems: "center", py: 0.25 }}
                        >
                            <Box
                                sx={{
                                    width: 14,
                                    height: 14,
                                    borderRadius: "50%",
                                    border: (theme) => `1px dashed ${theme.palette.text.disabled}`,
                                }}
                            />
                            <Typography
                                variant="caption"
                                sx={{ flexGrow: 1, fontStyle: "italic" }}
                            >
                                Generico (senza operatore)
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {oreGeneriche.toFixed(1)}h
                            </Typography>
                        </Stack>
                    )}
                </>
            )}

            {/* Placeholder di disponibilità attivi sul giorno */}
            {giornoDettaglio?.placeholders && giornoDettaglio.placeholders.length > 0 && (
                <>
                    <Divider sx={{ my: 0.5 }} />
                    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", mb: 0.25 }}>
                        <WarningAmberRounded sx={{ fontSize: 14, color: "warning.main" }} />
                        <Typography variant="caption" fontWeight="bold">
                            Placeholder attivi
                        </Typography>
                    </Stack>
                    {giornoDettaglio.placeholders.map((p: PlaceholderInfo, idx: number) => {
                        const positivo = p.ore >= 0;
                        const segno = positivo ? "+" : "";
                        const etichetta = p.tipo === "reparto"
                            ? "Reparto"
                            : p.tipo === "spostamento_entrante"
                                ? `${p.operatore_display ?? "?"} (entrante)`
                                : `${p.operatore_display ?? "?"} (uscente)`;
                        return (
                            <Stack
                                key={idx}
                                direction="row"
                                spacing={0.5}
                                sx={{ alignItems: "center", py: 0.15 }}
                            >
                                <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{ color: positivo ? "success.dark" : "error.dark", minWidth: 36 }}
                                >
                                    {segno}{p.ore.toFixed(1)}h
                                </Typography>
                                <Typography variant="caption" sx={{ flexGrow: 1 }} noWrap>
                                    {etichetta}
                                </Typography>
                            </Stack>
                        );
                    })}
                </>
            )}
        </Popover>
    );
};

// ============================================
// POPOVER PANORAMICA GIORNO (tutti i reparti)
// ============================================

interface PopoverPanoramicaGiornoProps {
    anchorEl: HTMLElement | null;
    onClose: () => void;
    reparti: CaricoRepartoGiorno[];
    oreDisponibiliTotali: number;
    oreSchedulateTotali: number;
}

const PopoverPanoramicaGiorno = ({
    anchorEl,
    onClose,
    reparti,
    oreDisponibiliTotali,
    oreSchedulateTotali,
}: PopoverPanoramicaGiornoProps) => {
    const percentualeTotale = oreDisponibiliTotali > 0 ? (oreSchedulateTotali / oreDisponibiliTotali) * 100 : 0;

    return (
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            slotProps={{
                paper: {
                    sx: { p: 1.5, minWidth: 220, maxWidth: 300 },
                },
            }}
        >
            {/* Totale */}
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
                <MiniPie percentuale={percentualeTotale} size={28} allarme={oreSchedulateTotali > oreDisponibiliTotali} />
                <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                        Totale
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {oreSchedulateTotali.toFixed(1)}h / {oreDisponibiliTotali.toFixed(1)}h
                    </Typography>
                </Box>
            </Stack>

            <Divider sx={{ my: 0.5 }} />

            {/* Lista reparti */}
            {reparti.map((reparto) => {
                const perc = reparto.ore_disponibili > 0
                    ? (reparto.ore_schedulate / reparto.ore_disponibili) * 100
                    : 0;
                return (
                    <Stack
                        key={reparto.reparto_id}
                        direction="row"
                        spacing={0.5}
                        sx={{ alignItems: "center", py: 0.25 }}
                    >
                        <MiniPie percentuale={perc} size={14} allarme={reparto.allarme} />
                        <Typography variant="caption" sx={{ flexGrow: 1 }} noWrap>
                            {reparto.codice}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {reparto.ore_schedulate.toFixed(1)}/{reparto.ore_disponibili.toFixed(1)}h
                        </Typography>
                    </Stack>
                );
            })}
        </Popover>
    );
};

// ============================================
// MINI PIE AGGREGATA CLICCABILE
// ============================================

interface MiniPieAggregataProps {
    percentuale: number;
    allarme: boolean;
    reparti: CaricoRepartoGiorno[];
    oreDisponibili: number;
    oreSchedulate: number;
}

const MiniPieAggregata = ({ percentuale, allarme, reparti, oreDisponibili, oreSchedulate }: MiniPieAggregataProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    return (
        <>
            <Box
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ cursor: "pointer", display: "inline-flex" }}
            >
                <MiniPie percentuale={percentuale} size={18} allarme={allarme} />
            </Box>
            <PopoverPanoramicaGiorno
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                reparti={reparti}
                oreDisponibiliTotali={oreDisponibili}
                oreSchedulateTotali={oreSchedulate}
            />
        </>
    );
};

// ============================================
// INDICATORE REPARTO (mini pie cliccabile)
// ============================================

const getColoreCarico = (percentuale: number, allarme: boolean, theme: any) => {
    if (allarme || percentuale >= 100) return theme.palette.error.main;
    if (percentuale >= 80) return theme.palette.warning.main;
    return theme.palette.success.main;
};

interface IndicatoreRepartoProps {
    reparto: CaricoRepartoGiorno;
    giornoDettaglio?: GiornoCalendarioDettaglio;
}

const IndicatoreReparto = ({ reparto, giornoDettaglio }: IndicatoreRepartoProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const percentuale = reparto.ore_disponibili > 0
        ? (reparto.ore_schedulate / reparto.ore_disponibili) * 100
        : 0;

    const haPlaceholder = (reparto.placeholder_count ?? 0) > 0;

    return (
        <>
            <Box
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ cursor: "pointer", display: "inline-flex", position: "relative" }}
            >
                <MiniPie percentuale={percentuale} size={18} allarme={reparto.allarme} />
                {haPlaceholder && (
                    <Tooltip title={`${reparto.placeholder_count} placeholder attivi`}>
                        <WarningAmberRounded
                            sx={{
                                position: "absolute",
                                top: -4,
                                right: -6,
                                fontSize: 12,
                                color: "warning.main",
                                backgroundColor: "background.paper",
                                borderRadius: "50%",
                            }}
                        />
                    </Tooltip>
                )}
            </Box>
            <PopoverCaricoReparto
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                codiceReparto={reparto.codice}
                descrizioneReparto={reparto.descrizione}
                giornoDettaglio={giornoDettaglio}
                giornoCalendario={reparto}
            />
        </>
    );
};

// ============================================
// POPOVER DETTAGLIO OPERATORE
// ============================================

interface PopoverCaricoOperatoreProps {
    anchorEl: HTMLElement | null;
    onClose: () => void;
    operatore: CaricoOperatoreGiorno;
    codiceReparto: string;
    descrizioneReparto: string;
}

const PopoverCaricoOperatore = ({
    anchorEl,
    onClose,
    operatore,
    codiceReparto,
    descrizioneReparto,
}: PopoverCaricoOperatoreProps) => {
    const percentuale = operatore.ore_disponibili > 0
        ? (operatore.ore_schedulate / operatore.ore_disponibili) * 100
        : 0;

    return (
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            slotProps={{
                paper: {
                    sx: { p: 1.5, minWidth: 200, maxWidth: 280 },
                },
            }}
        >
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
                <MiniPie percentuale={percentuale} size={28} allarme={operatore.allarme} />
                <Box>
                    <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        sx={{ textDecoration: operatore.assente ? "line-through" : "none" }}
                    >
                        {operatore.nominativo}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {codiceReparto} — {descrizioneReparto}
                    </Typography>
                </Box>
            </Stack>
            <Stack direction="row" spacing={2}>
                <Typography variant="caption">
                    Disponibili: <strong>{operatore.ore_disponibili.toFixed(1)}h</strong>
                </Typography>
                <Typography variant="caption">
                    Schedulate: <strong>{operatore.ore_schedulate.toFixed(1)}h</strong>
                </Typography>
            </Stack>
            {operatore.assente && (
                <Typography variant="caption" color="error.main" fontWeight="bold" sx={{ display: "block", mt: 0.5 }}>
                    Assente
                </Typography>
            )}
        </Popover>
    );
};

// ============================================
// INDICATORE OPERATORE (dot cliccabile)
// ============================================

interface IndicatoreOperatoreProps {
    operatore: CaricoOperatoreGiorno;
    codiceReparto: string;
    descrizioneReparto: string;
}

const IndicatoreOperatore = ({ operatore, codiceReparto, descrizioneReparto }: IndicatoreOperatoreProps) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const percentuale = operatore.ore_disponibili > 0
        ? (operatore.ore_schedulate / operatore.ore_disponibili) * 100
        : 0;
    const colore = operatore.assente
        ? theme.palette.grey[500]
        : getColoreCarico(percentuale, operatore.allarme, theme);

    return (
        <>
            <Box
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: colore,
                    cursor: "pointer",
                    border: `1px solid ${theme.palette.background.paper}`,
                    opacity: operatore.assente ? 0.6 : 1,
                    "&:hover": {
                        transform: "scale(1.3)",
                    },
                    transition: "transform 0.15s ease",
                }}
            />
            <PopoverCaricoOperatore
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                operatore={operatore}
                codiceReparto={codiceReparto}
                descrizioneReparto={descrizioneReparto}
            />
        </>
    );
};

// ============================================
// INDICATORE SENZA OPERATORE (carico generico per reparto)
// ============================================

interface IndicatoreSenzaOperatoreProps {
    codiceReparto: string;
    descrizioneReparto: string;
    oreGeneriche: number;
}

const IndicatoreSenzaOperatore = ({ codiceReparto, descrizioneReparto, oreGeneriche }: IndicatoreSenzaOperatoreProps) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    return (
        <>
            <Box
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: "transparent",
                    cursor: "pointer",
                    border: `1px dashed ${theme.palette.text.disabled}`,
                    "&:hover": {
                        transform: "scale(1.3)",
                    },
                    transition: "transform 0.15s ease",
                }}
            />
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
                slotProps={{
                    paper: { sx: { p: 1.5, minWidth: 200, maxWidth: 280 } },
                }}
            >
                <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
                    <Box
                        sx={{
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            border: `1px dashed ${theme.palette.text.disabled}`,
                        }}
                    />
                    <Box>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ fontStyle: "italic" }}>
                            (Senza operatore)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {codiceReparto} — {descrizioneReparto}
                        </Typography>
                    </Box>
                </Stack>
                <Typography variant="caption">
                    Schedulate sul reparto: <strong>{oreGeneriche.toFixed(1)}h</strong>
                </Typography>
            </Popover>
        </>
    );
};

// ============================================
// COMPONENT
// ============================================

const GanttSettimana = ({ giorni, calendario, repartiSelezionati, operatoriSelezionati, caricoDettaglio }: GanttSettimanaProps) => {
    const theme = useTheme();

    const filtroRepartiAttivo = !!(repartiSelezionati && repartiSelezionati.length > 0);
    const filtroOperatoriAttivo = !!(operatoriSelezionati && operatoriSelezionati.length > 0);
    const includiSenzaOperatore = !!(operatoriSelezionati && operatoriSelezionati.includes(SENZA_OPERATORE_ID));

    // Mappa veloce data -> giorno calendario
    const calendarioMap = useMemo(() => {
        const map: { [data: string]: GiornoCalendario } = {};
        calendario?.forEach((g) => {
            map[g.data] = g;
        });
        return map;
    }, [calendario]);

    // Mappa veloce (repartoId, data) -> giorno dettaglio
    const dettaglioMap = useMemo(() => {
        const map: { [key: string]: GiornoCalendarioDettaglio } = {};
        if (caricoDettaglio) {
            Object.entries(caricoDettaglio).forEach(([repartoId, dettaglio]) => {
                dettaglio.calendario?.forEach((g) => {
                    map[`${repartoId}_${g.data}`] = g;
                });
            });
        }
        return map;
    }, [caricoDettaglio]);

    return (
        <Grid container>
            {giorni.map((giorno) => {
                const dataISO = giorno.toISODate() || "";
                const isOggi = giorno.hasSame(DateTime.now(), "day");
                const giornoCalendario = calendarioMap[dataISO];
                const isLavorativo = giornoCalendario ? giornoCalendario.is_lavorativo : true;

                // Calcolo aggregato carico per il giorno
                let oreDisponibili = 0;
                let oreSchedulate = 0;
                let allarme = false;
                if (giornoCalendario && isLavorativo) {
                    giornoCalendario.reparti.forEach((r) => {
                        oreDisponibili += r.ore_disponibili;
                        oreSchedulate += r.ore_schedulate;
                        if (r.allarme) allarme = true;
                    });
                }
                const percentuale = oreDisponibili > 0 ? (oreSchedulate / oreDisponibili) * 100 : 0;

                // Reparti selezionati per questo giorno
                const repartiGiorno = filtroRepartiAttivo && giornoCalendario
                    ? giornoCalendario.reparti.filter((r) => repartiSelezionati!.includes(r.reparto_id))
                    : [];

                // Operatori selezionati per questo giorno (con info reparto di appartenenza).
                // Sorgente: caricoDettaglio dei reparti caricati (limitati ai reparti
                // selezionati se anche il filtro reparto è attivo).
                let operatoriGiorno: { operatore: CaricoOperatoreGiorno; codiceReparto: string; descrizioneReparto: string }[] = [];
                let genericoGiorno: { repartoId: number; codiceReparto: string; descrizioneReparto: string; oreGeneriche: number }[] = [];
                if (filtroOperatoriAttivo && caricoDettaglio) {
                    Object.values(caricoDettaglio).forEach((dettReparto) => {
                        if (filtroRepartiAttivo && !repartiSelezionati!.includes(dettReparto.reparto_id)) {
                            return;
                        }
                        const giornoDett = dettReparto.calendario.find((g) => g.data === dataISO);
                        if (!giornoDett) return;
                        giornoDett.operatori.forEach((op) => {
                            if (operatoriSelezionati!.includes(op.operatore_id)) {
                                operatoriGiorno.push({
                                    operatore: op,
                                    codiceReparto: dettReparto.codice,
                                    descrizioneReparto: dettReparto.descrizione,
                                });
                            }
                        });
                        if (includiSenzaOperatore) {
                            const oreSchedOp = giornoDett.operatori.reduce((s, op) => s + op.ore_schedulate, 0);
                            const oreGeneriche = Math.max(0, giornoDett.ore_schedulate - oreSchedOp);
                            if (oreGeneriche > 0) {
                                genericoGiorno.push({
                                    repartoId: dettReparto.reparto_id,
                                    codiceReparto: dettReparto.codice,
                                    descrizioneReparto: dettReparto.descrizione,
                                    oreGeneriche,
                                });
                            }
                        }
                    });
                }

                // Background
                let backgroundColor = theme.palette.background.paper;
                if (!isLavorativo) {
                    backgroundColor = theme.palette.grey[300];
                } else if (isOggi) {
                    backgroundColor = theme.palette.primary.light;
                }

                return (
                    <Grid
                        key={dataISO}
                        size={{ xs: 12 / 5 }}
                        sx={{
                            borderRight: `1px solid ${theme.palette.divider}`,
                            backgroundColor,
                            p: 0.5,
                            textAlign: "center",
                        }}
                    >
                        <Typography
                            variant="caption"
                            fontWeight={isOggi ? "bold" : "normal"}
                            color={
                                isOggi
                                    ? "primary.contrastText"
                                    : !isLavorativo
                                        ? "text.disabled"
                                        : "text.secondary"
                            }
                        >
                            {giorno.toFormat("EEE", { locale: "it" })}
                        </Typography>
                        <Typography
                            variant="body2"
                            fontWeight={isOggi ? "bold" : "normal"}
                            color={
                                isOggi
                                    ? "primary.contrastText"
                                    : !isLavorativo
                                        ? "text.disabled"
                                        : "text.primary"
                            }
                        >
                            {giorno.toFormat("dd")}
                        </Typography>

                        {/* Indicatori carico — solo giorni lavorativi con dati */}
                        {isLavorativo && giornoCalendario && (
                            <Box sx={{ display: "flex", justifyContent: "center", mt: 0.25, gap: 0.25, flexWrap: "wrap" }}>
                                {filtroOperatoriAttivo
                                    ? // Vista per operatore: pallino colorato con popover dettaglio
                                      // + pallino tratteggiato per il carico generico (sentinel "Senza operatore")
                                      <>
                                          {operatoriGiorno.map(({ operatore, codiceReparto, descrizioneReparto }) => (
                                              <IndicatoreOperatore
                                                  key={operatore.operatore_id}
                                                  operatore={operatore}
                                                  codiceReparto={codiceReparto}
                                                  descrizioneReparto={descrizioneReparto}
                                              />
                                          ))}
                                          {genericoGiorno.map(({ repartoId, codiceReparto, descrizioneReparto, oreGeneriche }) => (
                                              <IndicatoreSenzaOperatore
                                                  key={`generico-${repartoId}`}
                                                  codiceReparto={codiceReparto}
                                                  descrizioneReparto={descrizioneReparto}
                                                  oreGeneriche={oreGeneriche}
                                              />
                                          ))}
                                      </>
                                    : filtroRepartiAttivo
                                        ? // Vista per reparto: mini pie cliccabile con popover operatori
                                          repartiGiorno.map((reparto) => (
                                              <IndicatoreReparto
                                                  key={reparto.reparto_id}
                                                  reparto={reparto}
                                                  giornoDettaglio={dettaglioMap[`${reparto.reparto_id}_${dataISO}`]}
                                              />
                                          ))
                                        : oreDisponibili > 0 && (
                                              // Vista aggregata: mini pie cliccabile con popover reparti
                                              <MiniPieAggregata
                                                  percentuale={percentuale}
                                                  allarme={allarme}
                                                  reparti={giornoCalendario.reparti}
                                                  oreDisponibili={oreDisponibili}
                                                  oreSchedulate={oreSchedulate}
                                              />
                                          )}
                            </Box>
                        )}
                    </Grid>
                );
            })}
        </Grid>
    );
};

export default GanttSettimana;
